import {Stack, Box, HStack, Icon, Link, Spinner, ButtonProps} from "@chakra-ui/react";
import {
  MenuItem,
  MenuContent,
  MenuItemCommand,
  MenuRadioItemGroup,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  MenuRadioItem,
} from "./ui/menu";
import {Tooltip} from "./ui/tooltip";
import {usePromiseState} from "../hooks/promiseState";
import {findLanguage, splitLanguageBadge, useLanguagesForContexts} from "../App/i18n";
import {LanguageIcon, StrikethroughIcon} from "@heroicons/react/24/solid";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";
import {ComponentType, memo} from "react";

type LanguageMenuProps = {
  language: string;
  internalMode: boolean;
  onChangeLanguage: (languageCode: string) => Promise<void>;
  // The surrounding nav owns the trigger's styling.
  button: ComponentType<ButtonProps>;
  testId: string;
};

const LanguageMenu = memo(
  ({language, internalMode, onChangeLanguage, button: TriggerButton, testId}: LanguageMenuProps) => {
    const [languageCode, countryCode] = splitLanguageBadge(language);

    const [changingLanguage, changeLanguage] = usePromiseState(
      async (langCode: string | string[]) => {
        await onChangeLanguage(typeof langCode === "string" ? langCode : langCode[0]);
      },
      [onChangeLanguage],
    );

    const availableLanguages = useLanguagesForContexts("user", internalMode ? "internal" : undefined);
    const selectedLanguage = findLanguage(language);
    const t = useTranslate();

    return (
      <MenuRoot positioning={{placement: "bottom-end"}} lazyMount>
        <Tooltip
          content={selectedLanguage ? <T keyName={selectedLanguage.nameKey} /> : language}
          positioning={{placement: "bottom"}}
        >
          <Box>
            <MenuTrigger asChild>
              <TriggerButton data-testid={testId}>
                {changingLanguage.inProgress ? (
                  <Spinner size="sm" mt={1} />
                ) : (
                  <Stack textTransform="uppercase" gap={0} lineHeight="1.2">
                    <Box fontSize="xs">{languageCode}</Box>
                    {countryCode && <Box fontSize="xs">{countryCode}</Box>}
                  </Stack>
                )}
              </TriggerButton>
            </MenuTrigger>
          </Box>
        </Tooltip>
        <MenuContent>
          <MenuRadioItemGroup
            title={t("c.Select_language")}
            value={language}
            onValueChange={details => changeLanguage(details.value)}
          >
            {availableLanguages.map(lang => (
              <MenuRadioItem key={lang.code} value={lang.code}>
                <HStack as="span" w="full" gap={2}>
                  <Box flex="1">
                    <T keyName={lang.nameKey} />
                  </Box>
                  <MenuItemCommand>
                    <T keyName={lang.nameKey} language={lang.code} />
                  </MenuItemCommand>
                </HStack>
              </MenuRadioItem>
            ))}
          </MenuRadioItemGroup>
          <MenuSeparator />
          <MenuItem asChild value="report-bad-translation">
            <Link href="mailto:support+bad-translation@platformed.com" textDecoration="none !important">
              <Icon as={StrikethroughIcon} boxSize="1em" />
              <T keyName="c.Report_a_bad_translation" />
            </Link>
          </MenuItem>
          <MenuItem asChild value="request-new-language">
            <Link href="mailto:support+request-language@platformed.com" textDecoration="none !important">
              <Icon as={LanguageIcon} boxSize="1em" />
              <T keyName="c.Request_a_new_language" />
            </Link>
          </MenuItem>
        </MenuContent>
      </MenuRoot>
    );
  },
);

export default LanguageMenu;
